import React from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import DocumentTitle from 'react-document-title';
import TranslationContext from './contextProviders/TranslationContext';
import CSRFContext from './contextProviders/CSRFContext';
import StripeContext from './contextProviders/StripeContext';
import Header from './commonComponents/pageLayout/header/Header';
import Footer from './commonComponents/pageLayout/Footer';
import ScrollTop from './commonComponents/pageLayout/ScrollTop';
import NotFound from './sections/error/NotFound';
import Profile from './sections/profile/Profile';
import Trial from './sections/trial/Trial';
import SignIn from './sections/signIn/SignIn';
import Error from './sections/error/Error';
import SubscriptionDetails from './sections/subscriptionDetails/SubscriptionDetails';
import LoggedUserContext from './contextProviders/LoggedUserContext';
import RecoverPasswordForm from './sections/signIn/RecoverPasswordForm';
import StripeService from './services/stripeService';

/**
 * Wraps a component in the default page layout, header/footer, etc
 */
function wrapInDefaultLayout(Component, props) {
  const {
    theme,
    tenant,
    version,
    authOrganizations,
    organizationId,
    ...other
  } = props;
  return function (routeProps) {
    const withRoute = { ...other, ...routeProps };
    return (
      <>
        <Header
          theme={theme}
          authOrganizations={authOrganizations}
          organizationId={organizationId}
        />
        <Component {...withRoute} />
        <Footer version={version} tenant={tenant} />
        <ScrollTop />
      </>
    );
  };
}

/**
 * Renders the error page, using the error received from the server, or a client side error
 */
function renderError(props, clientSideError) {
  const finalProps = {
    ...clientSideError,
    ...props,
    status: props.status || 500,
  };
  return wrapInDefaultLayout(Error, finalProps)();
}

/**
 * React app entry point, creates context providers and an error boundary and
 * wraps most of the pages in a Header/Footer layout, as needed.
 */
export default class Main extends React.Component {
  constructor(props) {
    super(props);

    this.state = { hasError: false, error: undefined };
  }

  componentDidMount() {
    const { stripe = {}, favicon } = this.props;
    StripeService.init(stripe);

    const $favicon = document.querySelector('link[rel="shortcut icon"]');
    if ($favicon !== null && favicon) {
      $favicon.href = favicon;
    }
  }

  componentDidCatch(error, info) {
    this.setState({
      hasError: true,
      error: { message: error.toString(), stack: info.componentStack },
    });
  }

  render() {
    const { t, _csrf, stripe, profile } = this.props;
    const { hasError, error } = this.state;
    StripeService.init(stripe);
    return (
      <TranslationContext.Provider value={t}>
        <CSRFContext.Provider value={_csrf}>
          <StripeContext.Provider value={stripe}>
            <LoggedUserContext.Provider value={profile}>
              <DocumentTitle title={t('default_head_title')}>
                <div className="m-grid m-grid--hor m-grid--root m-page">
                  {hasError || this.props.error ? (
                    <>{renderError(this.props, { error })}</>
                  ) : (
                    <Switch>
                      <Route
                        path="/signin"
                        render={() => <SignIn {...this.props} />}
                      />
                      <Route
                        path="/password/recover/:token"
                        render={() => <RecoverPasswordForm {...this.props} />}
                      />
                      <Route
                        path="/profile"
                        render={wrapInDefaultLayout(Profile, this.props)}
                      />
                      <Route
                        path="/trial"
                        render={() => <Trial {...this.props} />}
                      />
                      <Route
                        exact
                        path="/payment/settings"
                        render={wrapInDefaultLayout(
                          SubscriptionDetails,
                          this.props,
                        )}
                      />
                      <Route
                        exact
                        path="/"
                        render={wrapInDefaultLayout(
                          SubscriptionDetails,
                          this.props,
                        )}
                      />
                      <Route
                        exact
                        path="*"
                        render={wrapInDefaultLayout(NotFound, this.props)}
                      />
                    </Switch>
                  )}
                </div>
              </DocumentTitle>
            </LoggedUserContext.Provider>
          </StripeContext.Provider>
        </CSRFContext.Provider>
      </TranslationContext.Provider>
    );
  }
}

Main.propTypes = {
  /** translation function, i18n.__ on the browser, response.__ on the server */
  t: PropTypes.func.isRequired,
  /** Cross site request forgery token, used on non-get requests to the server */
  _csrf: PropTypes.string,
  stripe: PropTypes.string,
  favicon: PropTypes.string.isRequired,
};

Main.defaultProps = {
  profile: null,
  _csrf: '',
  stripe: '',
};
